import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Pagination,
  InputAdornment,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextareaAutosize,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Add as AddIcon,
  LocalOffer as OfferIcon,
  Payments as PaymentsIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { hasActionPermission } from '../utils/permissions';
import affiliateService from '../services/affiliateService';

const AffiliateManagement = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0); // 0: Affiliates, 1: Offers, 2: Earnings
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedEarning, setSelectedEarning] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'approve', 'reject', 'view', 'edit', 'createOffer', 'payEarning', 'settings'
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);

  // Permissions
  const canViewAffiliates = hasActionPermission('view_affiliates', user?.permissions || [], user?.type);
  const canManageAffiliates = hasActionPermission('manage_affiliates', user?.permissions || [], user?.type);
  const canApproveAffiliates = hasActionPermission('approve_affiliates', user?.permissions || [], user?.type);
  const canManageOffers = hasActionPermission('manage_affiliate_offers', user?.permissions || [], user?.type);
  const canManageEarnings = hasActionPermission('manage_affiliate_earnings', user?.permissions || [], user?.type);

  // Fetch affiliates
  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: '20',
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const data = await affiliateService.getAllAffiliates(params);
      setAffiliates(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      setError('Failed to fetch affiliates: ' + (error.message || error));
      toast.error('Failed to fetch affiliates');
    } finally {
      setLoading(false);
    }
  };

  // Fetch offers
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: '20',
      };
      if (statusFilter) params.isActive = statusFilter;

      const data = await affiliateService.getAllOffers(params);
      setOffers(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      setError('Failed to fetch offers: ' + (error.message || error));
      toast.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch earnings
  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: '20',
      };
      if (statusFilter) params.status = statusFilter;

      const data = await affiliateService.getAllEarnings(params);
      setEarnings(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      setError('Failed to fetch earnings: ' + (error.message || error));
      toast.error('Failed to fetch earnings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const data = await affiliateService.getAffiliateStatistics();
      setStats(data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch settings
  const fetchSettings = async () => {
    try {
      const data = await affiliateService.getSettings();
      setSettings(data.data);
      setFormData(data.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  // Update settings
  const handleUpdateSettings = async () => {
    try {
      const data = await affiliateService.updateSettings(formData);
      setSettings(data.data);
      toast.success('Settings updated successfully');
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update settings');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSettings();
    if (tabValue === 0) {
      fetchAffiliates();
    } else if (tabValue === 1) {
      fetchOffers();
    } else {
      fetchEarnings();
    }
  }, [page, searchTerm, statusFilter, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
    setSearchTerm('');
    setStatusFilter('');
  };

  const handleApproveAffiliate = async () => {
    try {
      const data = await affiliateService.approveAffiliate(selectedAffiliate._id, formData);
      toast.success('Affiliate approved successfully');
      setDialogOpen(false);
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to approve affiliate');
    }
  };

  const handleRejectAffiliate = async () => {
    try {
      const data = await affiliateService.rejectAffiliate(selectedAffiliate._id, formData);
      toast.success('Affiliate rejected successfully');
      setDialogOpen(false);
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to reject affiliate');
    }
  };

  const handleSuspendAffiliate = async () => {
    try {
      const data = await affiliateService.suspendAffiliate(selectedAffiliate._id, formData);
      toast.success('Affiliate suspended successfully');
      setDialogOpen(false);
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to suspend affiliate');
    }
  };

  const handleCreateOffer = async () => {
    try {
      const data = await affiliateService.createOffer(formData);
      toast.success('Offer created successfully');
      setDialogOpen(false);
      setFormData({});
      fetchOffers();
    } catch (error) {
      toast.error(error.message || 'Failed to create offer');
    }
  };

  const handlePayEarning = async () => {
    try {
      const data = await affiliateService.payEarning(selectedEarning._id, formData);
      toast.success('Earning paid successfully');
      setDialogOpen(false);
      fetchEarnings();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to pay earning');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      suspended: 'error',
      paid: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount || 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Affiliate Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage affiliates, offers, and earnings
          </Typography>
        </Box>
        {canManageAffiliates && (
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => {
              setDialogType('settings');
              fetchSettings();
              setDialogOpen(true);
            }}
          >
            Settings
          </Button>
        )}
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Affiliates
                    </Typography>
                    <Typography variant="h5">
                      {stats.totalAffiliates || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Approved
                    </Typography>
                    <Typography variant="h5">
                      {stats.approvedAffiliates || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <WalletIcon color="warning" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Earnings
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(stats.totalEarnings || 0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Pending Earnings
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(stats.pendingEarnings || 0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PeopleIcon />} label="Affiliates" iconPosition="start" />
          <Tab icon={<OfferIcon />} label="Offers" iconPosition="start" />
          <Tab icon={<PaymentsIcon />} label="Earnings" iconPosition="start" />
        </Tabs>
      </Card>

      {/* Affiliates Tab */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            {/* Controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search affiliates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchAffiliates}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* Table */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Affiliate Code</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Total Sales</TableCell>
                        <TableCell>Total Earnings</TableCell>
                        <TableCell>Applied At</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {affiliates.map((affiliate) => (
                        <TableRow key={affiliate._id}>
                          <TableCell>
                            {affiliate.userId?.fullname || 'N/A'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              @{affiliate.userId?.username || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={affiliate.affiliateCode || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={affiliate.applicationStatus}
                              color={getStatusColor(affiliate.applicationStatus)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{affiliate.totalSales || 0}</TableCell>
                          <TableCell>{formatCurrency(affiliate.totalEarnings || 0)}</TableCell>
                          <TableCell>
                            {new Date(affiliate.appliedAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedAffiliate(affiliate);
                                  setDialogType('view');
                                  setDialogOpen(true);
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {affiliate.applicationStatus === 'pending' && canApproveAffiliates && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => {
                                      setSelectedAffiliate(affiliate);
                                      setDialogType('approve');
                                      setFormData({});
                                      setDialogOpen(true);
                                    }}
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedAffiliate(affiliate);
                                      setDialogType('reject');
                                      setFormData({});
                                      setDialogOpen(true);
                                    }}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {(affiliate.applicationStatus === 'suspended' || affiliate.applicationStatus === 'rejected') && canApproveAffiliates && (
                              <Tooltip title="Re-approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedAffiliate(affiliate);
                                    setDialogType('approve');
                                    setFormData({});
                                    setDialogOpen(true);
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {affiliate.applicationStatus === 'approved' && canManageAffiliates && (
                              <Tooltip title="Suspend">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => {
                                    setSelectedAffiliate(affiliate);
                                    setDialogType('suspend');
                                    setFormData({});
                                    setDialogOpen(true);
                                  }}
                                >
                                  <BlockIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Offers Tab */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box display="flex" gap={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchOffers}
                >
                  Refresh
                </Button>
              </Box>
              {canManageOffers && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDialogType('createOffer');
                    setFormData({
                      title: '',
                      description: '',
                      offerType: 'event_ticket',
                      rewardType: 'percentage',
                      rewardValue: 0,
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    });
                    setDialogOpen(true);
                  }}
                >
                  Create Offer
                </Button>
              )}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Reward</TableCell>
                        <TableCell>Sales</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {offers.map((offer) => (
                        <TableRow key={offer._id}>
                          <TableCell>
                            <Typography fontWeight="bold">{offer.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {offer.description?.substring(0, 50)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={offer.offerType} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {offer.rewardType === 'percentage'
                              ? `${offer.rewardValue}%`
                              : formatCurrency(offer.rewardValue)}
                          </TableCell>
                          <TableCell>{offer.performance?.sales || 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={offer.isActive ? 'Active' : 'Inactive'}
                              color={offer.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(offer.startDate).toLocaleDateString()} -{' '}
                            {new Date(offer.endDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedOffer(offer);
                                  setDialogType('viewOffer');
                                  setDialogOpen(true);
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {canManageOffers && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedOffer(offer);
                                      setDialogType('editOffer');
                                      setFormData({
                                        title: offer.title,
                                        description: offer.description,
                                        rewardType: offer.rewardType,
                                        rewardValue: offer.rewardValue,
                                        isActive: offer.isActive,
                                        startDate: new Date(offer.startDate).toISOString().split('T')[0],
                                        endDate: new Date(offer.endDate).toISOString().split('T')[0],
                                      });
                                      setDialogOpen(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={offer.isActive ? 'Deactivate' : 'Activate'}>
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      try {
                                        await affiliateService.toggleOfferStatus(offer._id);
                                        toast.success(`Offer ${offer.isActive ? 'deactivated' : 'activated'}`);
                                        fetchOffers();
                                      } catch (error) {
                                        toast.error(error.message || 'Failed to toggle offer status');
                                      }
                                    }}
                                  >
                                    <Switch
                                      checked={offer.isActive}
                                      size="small"
                                      readOnly
                                    />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Earnings Tab */}
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box display="flex" gap={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchEarnings}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Affiliate</TableCell>
                        <TableCell>Offer</TableCell>
                        <TableCell>Sale Amount</TableCell>
                        <TableCell>Commission</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {earnings.map((earning) => (
                        <TableRow key={earning._id}>
                          <TableCell>
                            {earning.affiliateId?.affiliateCode || 'N/A'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {earning.userId?.fullname || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{earning.offerId?.title || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(earning.saleAmount || 0)}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(earning.commissionAmount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={earning.status}
                              color={getStatusColor(earning.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(earning.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            {earning.status === 'pending' && canManageEarnings && (
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={async () => {
                                    try {
                                      await affiliateService.approveEarning(earning._id);
                                      toast.success('Earning approved');
                                      fetchEarnings();
                                    } catch (error) {
                                      toast.error(error.message || 'Failed to approve earning');
                                    }
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {earning.status === 'approved' && canManageEarnings && (
                              <Tooltip title="Pay">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedEarning(earning);
                                    setDialogType('payEarning');
                                    setFormData({
                                      paymentMethod: 'wallet',
                                      paymentReference: '',
                                    });
                                    setDialogOpen(true);
                                  }}
                                >
                                  <PaymentsIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'approve' && (selectedAffiliate?.applicationStatus === 'suspended' || selectedAffiliate?.applicationStatus === 'rejected' 
            ? 'Re-approve Affiliate' 
            : 'Approve Affiliate')}
          {dialogType === 'reject' && 'Reject Affiliate'}
          {dialogType === 'suspend' && 'Suspend Affiliate'}
          {dialogType === 'view' && 'Affiliate Details'}
          {dialogType === 'createOffer' && 'Create Affiliate Offer'}
          {dialogType === 'editOffer' && 'Edit Affiliate Offer'}
          {dialogType === 'payEarning' && 'Pay Earning'}
          {dialogType === 'settings' && 'Affiliate Settings'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'approve' && (
            <Box sx={{ pt: 2 }}>
              {selectedAffiliate && (
                <Alert 
                  severity={selectedAffiliate.applicationStatus === 'suspended' || selectedAffiliate.applicationStatus === 'rejected' ? 'warning' : 'info'} 
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>User:</strong> {selectedAffiliate.userId?.fullname} (@
                    {selectedAffiliate.userId?.username})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Applied:</strong>{' '}
                    {new Date(selectedAffiliate.appliedAt).toLocaleString()}
                  </Typography>
                  {(selectedAffiliate.applicationStatus === 'suspended' || selectedAffiliate.applicationStatus === 'rejected') && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Current Status:</strong> {selectedAffiliate.applicationStatus.charAt(0).toUpperCase() + selectedAffiliate.applicationStatus.slice(1)}
                    </Typography>
                  )}
                </Alert>
              )}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Commission Rate (%)</InputLabel>
                <TextField
                  type="number"
                  value={formData.commissionRate !== undefined ? formData.commissionRate : (selectedAffiliate?.commissionRate || '')}
                  onChange={(e) =>
                    setFormData({ ...formData, commissionRate: parseFloat(e.target.value) })
                  }
                  label="Commission Rate (%)"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText={selectedAffiliate?.applicationStatus === 'suspended' || selectedAffiliate?.applicationStatus === 'rejected' 
                    ? 'This will re-approve the affiliate and reactivate their account'
                    : 'Set the commission rate for this affiliate'}
                />
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes"
                value={formData.adminNotes || ''}
                onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                sx={{ mb: 2 }}
                helperText={selectedAffiliate?.applicationStatus === 'suspended' || selectedAffiliate?.applicationStatus === 'rejected'
                  ? 'Optional: Add notes about the re-approval'
                  : 'Optional: Add notes about the approval'}
              />
            </Box>
          )}

          {dialogType === 'reject' && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason"
                value={formData.rejectionReason || ''}
                onChange={(e) => setFormData({ ...formData, rejectionReason: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Admin Notes"
                value={formData.adminNotes || ''}
                onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
              />
            </Box>
          )}

          {dialogType === 'suspend' && (
            <Box sx={{ pt: 2 }}>
              {selectedAffiliate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>User:</strong> {selectedAffiliate.userId?.fullname} (@
                    {selectedAffiliate.userId?.username})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Affiliate Code:</strong> {selectedAffiliate.affiliateCode || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    This will suspend the affiliate and prevent them from earning commissions.
                  </Typography>
                </Alert>
              )}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Suspension Reason"
                value={formData.reason || ''}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
                sx={{ mb: 2 }}
                helperText="Please provide a reason for suspending this affiliate"
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Admin Notes"
                value={formData.adminNotes || ''}
                onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
              />
            </Box>
          )}

          {dialogType === 'view' && selectedAffiliate && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body1">
                    {selectedAffiliate.userId?.fullname} (@{selectedAffiliate.userId?.username})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Affiliate Code
                  </Typography>
                  <Typography variant="body1">
                    {selectedAffiliate.affiliateCode || 'Not generated'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedAffiliate.applicationStatus}
                    color={getStatusColor(selectedAffiliate.applicationStatus)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Sales
                  </Typography>
                  <Typography variant="body1">{selectedAffiliate.totalSales || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(selectedAffiliate.totalEarnings || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Commission Rate
                  </Typography>
                  <Typography variant="body1">
                    {selectedAffiliate.commissionRate || 0}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {dialogType === 'createOffer' && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Offer Type</InputLabel>
                <Select
                  value={formData.offerType || 'event_ticket'}
                  label="Offer Type"
                  onChange={(e) => setFormData({ ...formData, offerType: e.target.value })}
                >
                  <MenuItem value="event_ticket">Event Ticket</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="product">Product</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Reward Type</InputLabel>
                <Select
                  value={formData.rewardType || 'percentage'}
                  label="Reward Type"
                  onChange={(e) => setFormData({ ...formData, rewardType: e.target.value })}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label={formData.rewardType === 'percentage' ? 'Reward Percentage' : 'Reward Amount (₦)'}
                value={formData.rewardValue || 0}
                onChange={(e) =>
                  setFormData({ ...formData, rewardValue: parseFloat(e.target.value) })
                }
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate || ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.endDate || ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
          )}

          {dialogType === 'editOffer' && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Reward Type</InputLabel>
                <Select
                  value={formData.rewardType || 'percentage'}
                  label="Reward Type"
                  onChange={(e) => setFormData({ ...formData, rewardType: e.target.value })}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label={formData.rewardType === 'percentage' ? 'Reward Percentage' : 'Reward Amount (₦)'}
                value={formData.rewardValue || 0}
                onChange={(e) =>
                  setFormData({ ...formData, rewardValue: parseFloat(e.target.value) })
                }
                required
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate || ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.endDate || ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
          )}

          {dialogType === 'payEarning' && (
            <Box sx={{ pt: 2 }}>
              {selectedEarning && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {formatCurrency(selectedEarning.commissionAmount)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Affiliate:</strong> {selectedEarning.affiliateId?.affiliateCode}
                  </Typography>
                </Alert>
              )}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod || 'wallet'}
                  label="Payment Method"
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                >
                  <MenuItem value="wallet">Wallet</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Payment Reference"
                value={formData.paymentReference || ''}
                onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Box>
          )}

          {dialogType === 'settings' && (
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoApproveEarnings || false}
                    onChange={(e) =>
                      setFormData({ ...formData, autoApproveEarnings: e.target.checked })
                    }
                  />
                }
                label="Auto-approve earnings"
                sx={{ mb: 2, display: 'block' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 2 }}>
                Earnings will be automatically approved when created
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoPayEarnings || false}
                    onChange={(e) =>
                      setFormData({ ...formData, autoPayEarnings: e.target.checked })
                    }
                    disabled={!formData.autoApproveEarnings}
                  />
                }
                label="Auto-pay earnings"
                sx={{ mb: 2, display: 'block' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 2 }}>
                Automatically pay approved earnings (requires auto-approve to be enabled)
              </Typography>

              {formData.autoPayEarnings && (
                <TextField
                  fullWidth
                  type="number"
                  label="Auto-pay Threshold (₦)"
                  value={formData.autoPayThreshold || 0}
                  onChange={(e) =>
                    setFormData({ ...formData, autoPayThreshold: parseFloat(e.target.value) })
                  }
                  helperText="Minimum amount to trigger auto-payment"
                  sx={{ mb: 2 }}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled || true}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                }
                label="Affiliate system enabled"
                sx={{ mb: 2, display: 'block' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {dialogType === 'approve' && (
            <Button onClick={handleApproveAffiliate} variant="contained" color="success">
              {selectedAffiliate?.applicationStatus === 'suspended' || selectedAffiliate?.applicationStatus === 'rejected' 
                ? 'Re-approve' 
                : 'Approve'}
            </Button>
          )}
          {dialogType === 'reject' && (
            <Button onClick={handleRejectAffiliate} variant="contained" color="error">
              Reject
            </Button>
          )}
          {dialogType === 'suspend' && (
            <Button onClick={handleSuspendAffiliate} variant="contained" color="warning">
              Suspend
            </Button>
          )}
          {dialogType === 'createOffer' && (
            <Button onClick={handleCreateOffer} variant="contained">
              Create
            </Button>
          )}
          {dialogType === 'editOffer' && (
            <Button
              onClick={async () => {
                try {
                  await affiliateService.updateOffer(selectedOffer._id, formData);
                  toast.success('Offer updated successfully');
                  setDialogOpen(false);
                  fetchOffers();
                } catch (error) {
                  toast.error(error.message || 'Failed to update offer');
                }
              }}
              variant="contained"
            >
              Update
            </Button>
          )}
          {dialogType === 'payEarning' && (
            <Button onClick={handlePayEarning} variant="contained" color="primary">
              Pay
            </Button>
          )}
          {dialogType === 'settings' && (
            <Button onClick={handleUpdateSettings} variant="contained">
              Save Settings
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AffiliateManagement;

